import { useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { Upload, RefreshCw, Loader2, CheckCircle2, AlertTriangle } from "@/components/icons";

export default function ImportarClientesAtivos() {
  const { user } = useAuth();
  const [texto, setTexto] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [recalculando, setRecalculando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [recalcResult, setRecalcResult] = useState(null);

  if (user?.role !== "admin") return <Navigate to="/" replace />;

  const parseCnpjs = (raw) => {
    return raw
      .split(/[\n,;\t]/)
      .map((s) => s.trim())
      .filter(Boolean);
  };

  const onArquivo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const t = await f.text();
    setTexto((prev) => (prev ? prev + "\n" + t : t));
  };

  const enviar = async () => {
    const cnpjs = parseCnpjs(texto);
    if (!cnpjs.length) { toast.error("Informe pelo menos 1 CNPJ"); return; }
    setEnviando(true); setResultado(null);
    try {
      const { data } = await api.post("/admin/clientes-mpires/import", { cnpjs });
      setResultado(data);
      toast.success(`${data.marcados === 1 ? "1 cliente marcado" : `${data.marcados} clientes marcados`}, ${data.deals_atualizados === 1 ? "1 oportunidade atualizada" : `${data.deals_atualizados} oportunidades atualizadas`}.`);
    } catch (e) { toast.error(errMsg(e) || "Falha ao importar"); }
    finally { setEnviando(false); }
  };

  const recalcular = async () => {
    setRecalculando(true); setRecalcResult(null);
    try {
      const { data } = await api.post("/admin/cross-selling/recalcular");
      setRecalcResult(data);
      toast.success(`${data.atualizados === 1 ? "1 oportunidade atualizada" : `${data.atualizados} oportunidades atualizadas`}. ${data.mantidos_manual === 1 ? "1 mantida" : `${data.mantidos_manual} mantidas`} por ajuste manual.`);
    } catch (e) { toast.error(errMsg(e) || "Falha ao recalcular"); }
    finally { setRecalculando(false); }
  };

  return (
    <div>
      <PageHeader title="Importar clientes ativos MPires" subtitle="Cole ou anexe uma lista de CNPJs para marcar clientes já ativos e recalcular a ampliação de mix automaticamente." />

      <div className="bg-card rounded-2xl border border-black/5 p-5 shadow-[0_2px_10px_rgb(0,0,0,0.03)] mb-6" data-testid="importar-clientes-form">
        <label className="text-sm font-semibold text-navy mb-2 block">CNPJs (um por linha, ou separados por vírgula/ponto-e-vírgula)</label>
        <textarea
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          rows={8}
          placeholder="12.345.678/0001-99&#10;98.765.432/0001-10"
          data-testid="importar-cnpjs-textarea"
          className="w-full rounded-xl border border-input bg-white px-3 py-2 text-sm font-mono outline-none focus:ring-2 focus:ring-brand/40"
        />
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 h-10 px-3 rounded-lg border border-input text-sm font-medium hover:bg-slate-50 cursor-pointer" data-testid="importar-file-label">
            <Upload className="w-4 h-4" /> Anexar CSV
            <input type="file" accept=".csv,.txt" onChange={onArquivo} className="hidden" data-testid="importar-file-input" />
          </label>
          <button
            onClick={enviar}
            disabled={enviando || !texto.trim()}
            data-testid="importar-enviar-btn"
            className="h-10 px-5 rounded-lg bg-brand text-white text-sm font-semibold hover:bg-brand-hover disabled:opacity-60 flex items-center gap-2"
          >
            {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            Marcar como cliente MPires
          </button>
          <button
            onClick={() => { setTexto(""); setResultado(null); }}
            className="h-10 px-3 text-sm text-muted-foreground hover:text-navy"
          >Limpar</button>
        </div>

        {resultado && (
          <div className="mt-5 space-y-3" data-testid="importar-resultado">
            <div className="grid grid-cols-3 gap-3">
              <div className="rounded-xl bg-ok-soft p-3">
                <div className="text-xs text-ok/80 uppercase tracking-wider">Marcados agora</div>
                <div className="text-2xl font-bold text-ok" data-testid="res-marcados">{resultado.marcados}</div>
              </div>
              <div className="rounded-xl bg-slate-50 p-3">
                <div className="text-xs text-muted-foreground uppercase tracking-wider">Já eram MPires</div>
                <div className="text-2xl font-bold text-navy">{resultado.ja_marcados}</div>
              </div>
              <div className="rounded-xl bg-warn-soft p-3">
                <div className="text-xs text-warn/80 uppercase tracking-wider">Não encontrados</div>
                <div className="text-2xl font-bold text-warn" data-testid="res-nao-encontrados">{resultado.nao_encontrados.length}</div>
              </div>
            </div>
            <div className="text-xs text-muted-foreground">
              {resultado.deals_atualizados === 1 ? "1 oportunidade aberta recebeu" : `${resultado.deals_atualizados} oportunidades abertas receberam`} o selo de Ampliação de mix.
            </div>
            {resultado.nao_encontrados.length > 0 && (
              <details className="text-sm" data-testid="res-lista-nao-encontrados">
                <summary className="cursor-pointer text-navy font-medium flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-warn" /> CNPJs não encontrados ({resultado.nao_encontrados.length})</summary>
                <ul className="mt-2 pl-6 list-disc space-y-1 font-mono text-xs">
                  {resultado.nao_encontrados.map((c, i) => <li key={i}>{c || "(vazio)"}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>

      <div className="bg-card rounded-2xl border border-black/5 p-5 shadow-[0_2px_10px_rgb(0,0,0,0.03)]" data-testid="recalcular-bloco">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="text-sm font-semibold text-navy">Recalcular ampliação de mix</div>
            <div className="text-xs text-muted-foreground mt-1">Reavalia todas as oportunidades usando a regra atual. Ajustes manuais do dono são preservados.</div>
          </div>
          <button
            onClick={recalcular}
            disabled={recalculando}
            data-testid="recalcular-btn"
            className="h-10 px-4 rounded-lg border border-input text-navy text-sm font-semibold hover:bg-slate-50 disabled:opacity-60 flex items-center gap-2"
          >
            {recalculando ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            Recalcular agora
          </button>
        </div>
        {recalcResult && (
          <div className="mt-3 grid grid-cols-3 gap-3 text-sm" data-testid="recalcular-resultado">
            <div className="rounded-lg bg-slate-50 p-2"><div className="text-[11px] text-muted-foreground uppercase">Atualizadas</div><div className="font-bold text-navy">{recalcResult.atualizados}</div></div>
            <div className="rounded-lg bg-slate-50 p-2"><div className="text-[11px] text-muted-foreground uppercase">Inalteradas</div><div className="font-bold text-navy">{recalcResult.inalterados}</div></div>
            <div className="rounded-lg bg-slate-50 p-2"><div className="text-[11px] text-muted-foreground uppercase">Mantidas (manual)</div><div className="font-bold text-navy">{recalcResult.mantidos_manual}</div></div>
          </div>
        )}
      </div>
    </div>
  );
}
